import api from './api';
import type { SolutionClaim } from './subProjectService';

export interface SolutionClaimUpdate {
  claim_text?: string;
  category?: string;
  is_independent?: boolean;
  depends_on?: number[];
  claimable_item_id?: string | null;
}

export interface SessionItemDraft {
  item_id: string;
  item_label: string;
  item_category: string;
  independent_claim_text: string;
  aspects: string[];
}

export interface ClaimDraftingSession {
  id: string;
  sub_project_id: string;
  solution_id: string;
  created_at: string;
  expires_at: string;
  items: SessionItemDraft[];
}

export interface FinalizeItem {
  item_id: string;
  aspects: string[];
}

const base = (spId: string, solId: string) =>
  `/sub-projects/${spId}/solutions/${solId}/claims`;

export const solutionClaimsService = {
  async generateAspects(
    subProjectId: string,
    solutionId: string,
    force = false,
  ): Promise<ClaimDraftingSession> {
    const r = await api.post<ClaimDraftingSession>(
      `${base(subProjectId, solutionId)}/generate`,
      { solution_id: solutionId, force },
    );
    return r.data;
  },

  async finalizeClaims(
    subProjectId: string,
    solutionId: string,
    sessionId: string,
    items: FinalizeItem[],
  ): Promise<SolutionClaim[]> {
    const r = await api.post<SolutionClaim[]>(
      `${base(subProjectId, solutionId)}/generate/finalize`,
      { solution_id: solutionId, session_id: sessionId, items },
    );
    return r.data;
  },

  async getSession(
    subProjectId: string,
    solutionId: string,
    sessionId: string,
  ): Promise<ClaimDraftingSession> {
    const r = await api.get<ClaimDraftingSession>(
      `${base(subProjectId, solutionId)}/generate/sessions/${sessionId}`,
    );
    return r.data;
  },

  async generate(subProjectId: string, solutionId: string): Promise<SolutionClaim[]> {
    const r = await api.post<SolutionClaim[]>(`${base(subProjectId, solutionId)}/generate-simple`, { solution_id: solutionId });
    return r.data;
  },

  async list(subProjectId: string, solutionId: string): Promise<SolutionClaim[]> {
    const r = await api.get<SolutionClaim[]>(base(subProjectId, solutionId) + '/');
    return r.data;
  },

  async update(subProjectId: string, solutionId: string, claimId: string, payload: SolutionClaimUpdate): Promise<SolutionClaim> {
    const r = await api.put<SolutionClaim>(`${base(subProjectId, solutionId)}/${claimId}`, payload);
    return r.data;
  },

  async decompose(subProjectId: string, solutionId: string, claimId: string): Promise<SolutionClaim> {
    const r = await api.post<SolutionClaim>(`${base(subProjectId, solutionId)}/${claimId}/decompose`, {});
    return r.data;
  },

  async delete(subProjectId: string, solutionId: string, claimId: string): Promise<void> {
    await api.delete(`${base(subProjectId, solutionId)}/${claimId}`);
  },
};
